import axios, {AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosResponseTransformer} from 'axios';
import queryStringify from 'qs-stringify';
import store from 'store2';
import {requestErrorToast} from '../AppInterface/elements/Toasts/Toasts';
import {i18n} from '../AppInterface/lib/i18n';
import {isTrueObject} from '../AppInterface/lib/helpers/isTrueObject';
import {downloadDataAsFile} from '../AppInterface/lib/helpers/downloadDataAsFile';

axios.defaults.withCredentials = true;
axios.defaults.xsrfHeaderName = 'X-CSRF-TOKEN';
axios.defaults.xsrfCookieName = 'CSRF-TOKEN';
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// Toast debouncing: Prevents duplicate toasts within a time window
const recentToasts = new Map<string, boolean>();
const TOAST_DEBOUNCE_TIME = 6000; // toast display duration is 5 seconds

function showDebouncedToast(message: string): void {
  if (recentToasts.has(message)) {
    return; // Toast was recently shown
  }

  requestErrorToast(message);
  recentToasts.set(message, true);

  setTimeout(() => recentToasts.delete(message), TOAST_DEBOUNCE_TIME);
}

export interface TRequestError {
  status?: number;
  errors?: string[];

  [key: string]: unknown;
}

// form endpoints answer with a field -> error code map instead of a list
export type TFormRequestError = Omit<TRequestError, 'errors'> & {errors?: Record<string, unknown>};

interface TRequestOptions extends AxiosRequestConfig {
  background?: boolean;
}

type TQueryParams = object | URLSearchParams | null;
type TRequestData = object | FormData | string | null;

const SOURCE_HASH_HEADER = 'X-Source-Hash';

// Extracts the pathname from the hash, ignoring query params and trailing slashes.
// e.g. '#/polls/123?tab=2' → '/polls/123'
function getHashPathname(): string {
  return window.location.hash.replace(/^#/, '').split('?')[0].replace(/\/$/, '') || '/';
}

export class Request {
  private axios: AxiosInstance;

  constructor() {
    this.axios = axios.create({
      xsrfHeaderName: 'X-CSRF-TOKEN',
      xsrfCookieName: 'CSRF-TOKEN',
    });

    this.axios.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        const requestPathname = error.config?.headers?.[SOURCE_HASH_HEADER] as string | undefined;
        const currentPathname = getHashPathname();

        if (requestPathname && requestPathname !== currentPathname) {
          console.warn(`Request from path '${requestPathname}' failed: ${error}`);
          // Page has changed since the request was sent — suppress toast
          return Promise.reject(error);
        }

        if (!error.response) {
          showDebouncedToast(i18n.t('errors.serverTimeout', {ns: 'common'}));
          return;
        } else if (error.response.status) {
          //todo: second condition just for testing, remove later
          if (error.response.status === 401 && !error.config?.url?.includes('/reports/')) {
            window.location.href = '/';
            return;
          } else if (error.response.status === 403) {
            showDebouncedToast(i18n.t('errors.noAccess', {ns: 'common'}));
          } else if (error.response.status === 404) {
            showDebouncedToast(i18n.t('errors.NotFound', {ns: 'common'}));
          }
          // 	500 Internal Server Error
          // 	501 Not Implemented
          // 	502 Bad Gateway
          // 	503 Service Unavailable
          // 	504 Gateway Timeout
          // 	505 HTTP Version Not Supported
          // 	506 Variant Also Negotiates (RFC 2295)
          // 	507 Insufficient Storage (WebDAV; RFC 4918)
          // 	508 Loop Detected (WebDAV; RFC 5842)
          // 	510 Not Extended (RFC 2774)
          // 511 Network Authentication Required (RFC 6585)
          else if (error.response.status >= 500 && error.response.status < 600) {
            if (error.response.status === 502) {
              showDebouncedToast(i18n.t('errors.serverBadGateway', {ns: 'common'}));
            } else if (error.response.status === 504) {
              showDebouncedToast(i18n.t('errors.serverTimeout', {ns: 'common'}));
            } else {
              showDebouncedToast(i18n.t('errors.serverError', {ns: 'common'}));
            }
          }
        } else if (error.response) {
          showDebouncedToast(i18n.t('errors.serverError', {ns: 'common'}));
        }

        return Promise.reject(error);
      }
    );
  }

  get<T = unknown>(url: string, data: TQueryParams, options?: AxiosRequestConfig): Promise<T> {
    return new Promise((resolve, reject) => {
      if (isTrueObject(data)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let queryString = queryStringify(data as any);
        url += url.includes('?') ? `&${queryString}` : `?${queryString}`;
      }
      this.axios
        .get<T>(url, options)
        .then((response) => {
          this.storeAppValues(response.headers);
          resolve(response.data);
        })
        .catch(({response}) => reject(this.getRequestErrorResult(response)));
    });
  }

  getResponse<T = unknown>(url: string, data: TQueryParams, options?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return new Promise((resolve, reject) => {
      if (isTrueObject(data)) {
        let queryString: string;
        if (data instanceof URLSearchParams) {
          queryString = data.toString();
        } else {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          queryString = queryStringify(data as any);
        }
        url += url.includes('?') ? `&${queryString}` : `?${queryString}`;
      }
      this.axios
        .get<T>(url, options)
        .then((response) => {
          this.storeAppValues(response.headers);
          // Header values arrive as strings; parse once so consumers can rely on a number
          if (response.headers['x-total-count'] !== undefined) {
            response.headers['x-total-count'] = parseInt(String(response.headers['x-total-count']), 10);
          }
          resolve(response);
        })
        .catch(({response}) => reject(this.getRequestErrorResult(response)));
    });
  }

  post<T = unknown>(url: string, data?: TRequestData, options?: AxiosRequestConfig): Promise<T> {
    return new Promise((resolve, reject) => {
      this.axios
        .post<T>(url, data ?? null, options)
        .then((response) => {
          this.storeAppValues(response.headers);
          resolve(response.data);
        })
        .catch(({response}) => {
          reject(this.getRequestErrorResult(response));
        });
    });
  }

  patch<T = unknown>(url: string, data?: TRequestData, options?: AxiosRequestConfig): Promise<T> {
    return new Promise((resolve, reject) => {
      this.axios
        .patch<T>(url, data ?? null, options)
        .then((response) => {
          this.storeAppValues(response.headers);
          resolve(response.data);
        })
        .catch(({response}) => {
          reject(this.getRequestErrorResult(response));
        });
    });
  }

  put<T = unknown>(url: string, data?: TRequestData, options?: AxiosRequestConfig): Promise<T> {
    return new Promise((resolve, reject) => {
      this.axios
        .put<T>(url, data ?? null, options)
        .then((response) => {
          this.storeAppValues(response.headers);
          resolve(response.data);
        })
        .catch(({response}) => {
          reject(this.getRequestErrorResult(response));
        });
    });
  }

  delete<T = unknown>(url: string, data: TRequestData, options?: AxiosRequestConfig): Promise<T> {
    options = options || {};
    options.data = data ?? null;
    return new Promise((resolve, reject) => {
      this.axios
        .delete<T>(url, options)
        .then((response) => {
          this.storeAppValues(response.headers);
          resolve(response.data);
        })
        .catch(({response}) => {
          reject(this.getRequestErrorResult(response));
        });
    });
  }

  download(url: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      this.axios
        .get<Blob>(url, {responseType: 'blob'})
        .then((response) => {
          const disposition = response.headers['content-disposition'] ?? '';
          // RFC 5987: prefer filename*= (percent-encoded UTF-8) over filename= to support non-ASCII characters
          const rfc5987Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
          const legacyMatch = disposition.match(/filename="?([^";]+)"?/i);
          const filename = rfc5987Match ? decodeURIComponent(rfc5987Match[1].trim()) : (legacyMatch?.[1]?.trim() ?? '');
          const contentType = filename.split('.').reverse()[0];

          downloadDataAsFile(filename, response.data, contentType);
          resolve(response.data);
        })
        .catch(({response}) => reject(this.getRequestErrorResult(response)));
    });
  }

  //getRequestErrorResult nötig für
  // - Weitergabe der Fehlermeldungen für Frontend, z.B. Forms
  // - Error handling for legacy code (e.g. lp.poll.js)
  getRequestErrorResult(response?: AxiosResponse): TRequestError {
    let result: TRequestError;

    // return our own error message if the response code is 400
    if (response && [400, 409].includes(response.status)) {
      result = {errors: response.data.errors ?? [response.data.error]};
      return result;
    }

    if (response && response.status) {
      // response.data can be a non-JSON string (e.g., an HTML error page from an intermediary).
      // Spreading a string would add one property per character, freezing the tab on large bodies -
      // keep the body as (flattened) error entries instead (no freeze, no information loss).
      if (isTrueObject(response.data)) {
        result = {status: response.status, ...response.data};
      } else if (Array.isArray(response.data) && response.data.length > 0) {
        result = {status: response.status, errors: response.data};
      } else {
        result = {status: response.status, errors: [response.data || 'serverError']};
      }
    } else {
      result = {errors: [response ? 'serverError' : 'serverTimeout']};
    }
    return result;
  }

  storeAppValues(headers: Record<string, unknown>): void {
    const prefix = 'app-';
    // @ts-expect-error - filterKeys is a custom method on headers object
    headers.filterKeys(prefix + '*').foreach((value: unknown, key: string) => {
      store.set(key.slice(prefix.length), value);
    });
  }
}

const request = new Request();

function processOptions(options?: TRequestOptions): AxiosRequestConfig | undefined {
  if (options?.background) {
    options.headers = {...options.headers, Background: 1};
    delete options.background;
  }
  return options;
}

function processGetOptions(options?: TRequestOptions): AxiosRequestConfig | undefined {
  options = options || {};
  options.headers = {
    ...options.headers,
    [SOURCE_HASH_HEADER]: getHashPathname(),
    ...(options.background ? {Background: 1} : {}),
  };
  delete options.background;
  return options;
}

// For Typescript: Axios calls transformResponse fns as (data, headers, status) - JSON.parse's own (text, reviver) signature clashes with that, so wrap it
export const parseJsonResponse: AxiosResponseTransformer = (data: string) => JSON.parse(data);

export function post<T = unknown>(url: string, data?: TRequestData, options?: TRequestOptions): Promise<T> {
  return request.post<T>(url, data, processOptions(options));
}

// Legacy endpoints read PHP form fields directly, including survey session restoration.
export function postForm<T = unknown>(url: string, data?: TRequestData, options?: TRequestOptions): Promise<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const payload = isTrueObject(data) && !(data instanceof FormData) ? queryStringify(data as any) : data;
  return post<T>(url, payload, options);
}

export function patch<T = unknown>(url: string, data?: TRequestData, options?: TRequestOptions): Promise<T> {
  return request.patch<T>(url, data, processOptions(options));
}

export function put<T = unknown>(url: string, data?: TRequestData, options?: TRequestOptions): Promise<T> {
  return request.put<T>(url, data, processOptions(options));
}

export function download(url: string): Promise<Blob> {
  return request.download(url);
}

export function get<T = unknown>(url: string, queryParams?: TQueryParams, options?: TRequestOptions): Promise<T> {
  return request.get<T>(url, queryParams || null, processGetOptions(options));
}

export function getResponse<T = unknown>(url: string, data?: TQueryParams, options?: TRequestOptions): Promise<AxiosResponse<T>> {
  return request.getResponse<T>(url, data || null, processGetOptions(options));
}

export function del<T = unknown>(url: string, data?: TRequestData, options?: TRequestOptions): Promise<T> {
  return request.delete<T>(url, data || null, processOptions(options));
}
